import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useMediaQuery } from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import Select from "../../../components/Form/Select";
import { formattedIframeByType } from "../../../services/adsCampaigns";
import { startGetCampaignApprovalAdPreview } from "../../../actions/campaignApproval";
import {
  FORMAT_PLACEHOLDER,
  META_AD_PREVIEW_AD_BLOCKER_NOTICE,
} from "../../../i18n/keysTranslations";
import {
  getAvailableApprovalAdFormats,
  getDefaultApprovalAdFormat,
} from "../utils/adPreviewFormats";
import {
  getCachedCampaignApprovalAdPreview,
  setCachedCampaignApprovalAdPreview,
} from "../utils/campaignApprovalAdPreviewCache";

const CampaignApprovalAdPreview = ({
  token = "",
  campaignID = "",
  platform = "",
  entityID = "",
  isExpanded = false,
  isCarousel = false,
  callToAction = "",
  mediaUrl = "",
  fallback = null,
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const isMobile = useMediaQuery(() => "(max-width: 360px)");
  const abortControllerRef = useRef(null);

  const availableFormats = useMemo(() => {
    return getAvailableApprovalAdFormats({
      platform,
      isCarousel,
      callToAction,
      mediaUrl,
    });
  }, [platform, isCarousel, callToAction, mediaUrl]);

  const defaultFormat = useMemo(() => {
    return getDefaultApprovalAdFormat({
      platform,
      isCarousel,
      callToAction,
      mediaUrl,
    });
  }, [platform, isCarousel, callToAction, mediaUrl]);

  const { control, errors, watch, setValue } = useForm({
    defaultValues: {
      adFormat: defaultFormat,
    },
  });
  const adFormat = watch("adFormat");

  const [previewSrc, setPreviewSrc] = useState("");
  const [hasPreviewFailed, setHasPreviewFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const [iframeLoadFailed, setIframeLoadFailed] = useState(false);

  useEffect(() => {
    if (!defaultFormat) {
      return;
    }

    if (!availableFormats.includes(adFormat)) {
      setValue("adFormat", defaultFormat);
    }
  }, [adFormat, availableFormats, defaultFormat, setValue]);

  const loadPreview = useCallback(async () => {
    if (
      !token ||
      !campaignID ||
      !platform ||
      !entityID ||
      !adFormat ||
      !isExpanded
    ) {
      return;
    }

    const cacheParams = { platform, entityID, adFormat };
    const cachedPreview = getCachedCampaignApprovalAdPreview(cacheParams);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setHasPreviewFailed(false);
    setIframeLoadFailed(false);
    setIsLoadingIframe(true);

    if (cachedPreview) {
      setPreviewSrc(cachedPreview);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setPreviewSrc("");

    const response = await dispatch(
      startGetCampaignApprovalAdPreview(
        {
          token,
          campaignID,
          platform,
          entityID,
          adFormat,
        },
        abortController.signal
      )
    );

    if (abortController.signal.aborted) {
      return;
    }

    setIsLoading(false);

    if (!response?.ok) {
      setPreviewSrc("");
      setHasPreviewFailed(true);
      return;
    }

    const nextPreview = response?.adPreviews?.[adFormat] || "";
    setPreviewSrc(nextPreview);

    if (nextPreview) {
      setCachedCampaignApprovalAdPreview(cacheParams, nextPreview);
    } else {
      setHasPreviewFailed(true);
    }
  }, [adFormat, campaignID, dispatch, entityID, isExpanded, platform, token]);

  useEffect(() => {
    if (!isExpanded) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      return;
    }

    loadPreview();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isExpanded, loadPreview]);

  const { height, width, src } = formattedIframeByType({
    type: adFormat,
    iframe: previewSrc || "",
    isMobile,
    isCarousel,
    platform,
  });

  useEffect(() => {
    if (!isLoading && previewSrc && !src) {
      setHasPreviewFailed(true);
    }
  }, [isLoading, previewSrc, src]);

  const formatOptions = availableFormats.map((format) => ({
    value: format,
    label: t(format),
  }));

  const showFallback =
    !isExpanded ||
    (!isLoading &&
      (hasPreviewFailed || iframeLoadFailed || (!previewSrc && !isLoading)));

  const showPreview = isExpanded && !showFallback && Boolean(src);
  const showPreviewLoader = isLoading || isLoadingIframe;

  if (showFallback) {
    return fallback;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      {availableFormats.length > 1 ? (
        <Box sx={{ mb: 1.5, width: "100%", maxWidth: 280 }}>
          <Select
            variant="filled"
            name="adFormat"
            label={t(FORMAT_PLACEHOLDER)}
            control={control}
            errors={errors}
            options={formatOptions}
            disabled={isLoading}
            fullWidth={true}
            doTranslate={false}
          />
        </Box>
      ) : null}

      <Box
        sx={{
          width,
          height,
          position: "relative",
          alignSelf: "flex-start",
        }}
      >
        {showPreviewLoader ? (
          <>
            <Skeleton
              variant="rectangular"
              sx={{
                width,
                height,
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 3,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 4,
              }}
            >
              <CircularProgress size={36} />
            </Box>
          </>
        ) : null}

        {src ? (
          <iframe
            title="campaign-approval-ad-preview"
            src={src}
            scrolling="no"
            onLoad={() => setIsLoadingIframe(false)}
            onError={() => {
              setIframeLoadFailed(true);
              setIsLoadingIframe(false);
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              border: isMobile ? "none" : "1px solid white",
              overflow: "hidden",
              maxWidth: width,
              width: "100%",
              height,
              zIndex: 2,
            }}
          />
        ) : null}
      </Box>

      {showPreview && !isLoading && !isLoadingIframe ? (
        <Box
          sx={{
            mt: 1,
            p: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "warning.main",
            alignSelf: "stretch",
            maxWidth: width,
          }}
        >
          <Typography
            component="p"
            variant="caption"
            sx={{
              m: 0,
              color: "warning.dark",
              fontSize: "0.72rem",
              lineHeight: 1.4,
            }}
          >
            {t(META_AD_PREVIEW_AD_BLOCKER_NOTICE)}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default CampaignApprovalAdPreview;
